import { AxiosError } from "axios";
import { i18n } from "next-i18next";
import { NextRouter } from "next/router";
import {
  alert,
  confirmReceipt,
  createLinkTrackingReceipt,
  createReceipt,
} from "../../../utils/APICalls/APICalls";
import { getCookie } from "../../../utils/CookieHelper";
import { refreshOutdatedItems } from "../../../utils/cart/refreshOutdatedItems";
import {
  AlertErrorType,
  ErrorType,
  LowErrorType,
  SuccessReportTypes,
} from "../../../utils/ErrorTypes";
import Offer from "../../../utils/OfferClass/Offer/Offer";
import {
  COOKIE_ENUM,
  MAIN_OFFER_TYPE,
  PAYMENT_METHOD,
  PagesEnum,
  PayTypeEnum,
  URL_PARAMS,
} from "../../../utils/enums";
import envConfig from "../../../utils/env.config";
import { getPageLink } from "../../../utils/help/navigation/navigation";
import {
  isVisitor,
  monitoringIdentifiers,
} from "../../../utils/help/user/user";
import {
  armGatewayDepartureTracking,
  buildConfirmFingerprint,
  getReusablePendingOnlinePayment,
  storePendingOnlinePayment,
  walletOrderFingerprint,
} from "../../../utils/pendingOnlinePayment";
import { offersCache } from "../../../utils/simpleCache";
import {
  PAGER_DUTY_ERROR_URGENCY,
  SubItemType,
  confirmReceiptBodyType,
} from "../../../utils/types";
import { CreateReceiptBodyTypeFlat } from "../../../utils/types/createReceipt";
import { AppDispatch } from "../../redux/app/store";
import {
  resetAllOffersArrays,
  resetSelectedScheduleOptions,
  setOfferErrorReason,
  setOutdatedSubItems,
  setSelectedScheduleSlot,
  updateOutOfStockItemsDetails,
} from "../../redux/slices/cartAndLoyaltySlice";
import {
  receiptErrorAction,
  removeLoyaltyOffer,
  removeOfferAction,
  setLoyaltyOfferError,
} from "../../redux/slices/cartLoyaltyV2Slice";
import {
  LoadingActionEnum,
  decreasePendingOperations,
  pushToLoadingStack,
  setAnimatedGlobalBannerState,
  setBottomSheetState,
  setLoading,
} from "../../redux/slices/globalComponentsToggleSlice";
import {
  setCurrentReceiptId,
  setPOSPayType,
  setPosReceiptPaymentTimeout,
  setTimeRemainingToSendAnotherSession,
} from "../../redux/slices/receiptSlice";
import { setReferenceName } from "../../redux/slices/referenceSlice";
import { startDsqpwpSession } from "../../redux/slices/dsqPayWithPointsSlice";

const token = getCookie(COOKIE_ENUM.TOKEN);

export const confirmReceiptRequest = async (
  requestBody: confirmReceiptBodyType | CreateReceiptBodyTypeFlat,
  dispatch: AppDispatch,
  referenceName: string,
  router: NextRouter,
  redirectPage?: PagesEnum,
  redeemedOffer?: Offer,
  selfService?: boolean,
  loadingStateSolution?: boolean,
  v2Options?: { useV2: boolean }
) => {
  // Fallback duplicate-order guard. Coming back from the gateway normally
  // cancels the unpaid order and clears this stamp (see
  // useReleaseAbandonedPayment), so pressing Pay again confirms a brand new
  // order. If that release did not happen, the previous order is still alive and
  // awaiting payment — reopen its payment session rather than confirming again,
  // which would create a second receipt and send the restaurant a duplicate.
  // Wallet-paid orders are excluded: their wallet hold is released when the
  // customer backs out (useReleaseAbandonedPayment), so their session must not
  // be reused.
  const usesWallet = "useWallet" in requestBody && requestBody.useWallet;
  const confirmFingerprint =
    requestBody.paymentMethod === PAYMENT_METHOD.ONLINE_CARD && !usesWallet
      ? buildConfirmFingerprint(requestBody)
      : "";
  const pendingPayment = getReusablePendingOnlinePayment(confirmFingerprint);
  if (pendingPayment) {
    if (pendingPayment.receiptId) {
      dispatch(setCurrentReceiptId(pendingPayment.receiptId));
    }
    if (loadingStateSolution) {
      dispatch(
        pushToLoadingStack({
          action: LoadingActionEnum.DECREASE,
          functionName: "usePayHelper confirmReceiptRequest",
          date: new Date(),
        })
      );
      dispatch(decreasePendingOperations());
    } else {
      dispatch(setLoading(false));
    }
    // Reopening the same payment session is a trip to the gateway like any
    // other — the abandon that follows it must be proven the same way.
    armGatewayDepartureTracking();
    window.location.href = pendingPayment.paymentLink;

    return;
  }

  const request = v2Options?.useV2
    ? createReceipt(requestBody as CreateReceiptBodyTypeFlat)
    : confirmReceipt(requestBody as confirmReceiptBodyType);

  await request
    .then((res) => {
      let queryParams;
      if (res.data.receiptId) {
        dispatch(setCurrentReceiptId(res.data.receiptId));
        if (referenceName.length > 0) {
          createLinkTrackingReceipt(referenceName, res.data.receiptId);
          dispatch(setReferenceName(""));
        }
        dispatch(resetSelectedScheduleOptions());
        if (token) {
          monitoringIdentifiers(token);
        }
      }
      if (res.status === 210) {
        switch (res.data.reason) {
          case SuccessReportTypes.OperatingHoursService_CheckOperatingHoursPerFranchise_ClosedFranchise:
          case SuccessReportTypes.OperatingHoursService_CheckOperatingHoursPerFranchise_ClosedDelivery:
            window.location.href = getPageLink(router, PagesEnum.Home, {}).as;

            return;
          case SuccessReportTypes.DeliveryService_CheckDistanceFromRestaurant_AddressOutOfOperatingRadius:
            // set globel outofZone --> route him 3la el location page ? --> Banner -->without  Params  and remove Use Effect  #1805
            window.location.href = getPageLink(router, PagesEnum.Location, {
              outOfZone: true,
            }).as;

            return;

          case SuccessReportTypes.ReceiptController_ConfirmReceipt_itemQuantityOutOfStock:
          case SuccessReportTypes.ReceiptController_ConfirmReceipt_itemsQuantityOutOfStock:
            queryParams = [`${URL_PARAMS.ITEMS_OUT_OF_STOCK}=true`];

            dispatch(
              updateOutOfStockItemsDetails({
                title:
                  res.data.data.uiDetails.title[
                    i18n?.language || envConfig.defaultLanguage
                  ],
                body: res.data.data.uiDetails.body[
                  i18n?.language || envConfig.defaultLanguage
                ],
                conditionalText:
                  res.data.data.uiDetails.conditionalText[
                    i18n?.language || envConfig.defaultLanguage
                  ],
                outOfStockItems: res.data.data.itemsQuantityLeft,
              })
            );

            router.push(
              getPageLink(router, PagesEnum.Cart, { queryParams }).href,
              getPageLink(router, PagesEnum.Cart, { queryParams }).as
            );

            return;

          case SuccessReportTypes.ReceiptController_ConfirmReceipt_ReqItemsContainsOutDatedItems:
          case SuccessReportTypes.ItemService_ValidateOutOfStockSubItemIds_ReqItemsContainsOutOfStock:
            queryParams = [`${URL_PARAMS.OUTDATED_ITEMS}=true`];
            dispatch(
              receiptErrorAction({
                errorType: res.data.reason,
                subItemIds: res.data.data.subItems.map(
                  (subItem: SubItemType) => subItem.id
                ),
                outOfStockItems: [],
              })
            );

            if (res.data.data.subItems) {
              dispatch(setOutdatedSubItems(res.data.data.subItems));
            }
            // Without this the guest re-adds from the same stale lookup and is
            // rejected again; see refreshOutdatedItems. Out-of-stock shares
            // this branch but the refetch is harmless there.
            refreshOutdatedItems(dispatch, res.data.data.subItems).catch(
              () => undefined
            );
            router.push(
              getPageLink(router, PagesEnum.Cart, { queryParams }).href,
              getPageLink(router, PagesEnum.Cart, { queryParams }).as
            );

            return;

          case SuccessReportTypes.LoyaltyPackageServiceHelper_CheckPackageOfferVlaidity_MoreThanOneFreeItem:
          case SuccessReportTypes.LoyaltyPackageService_GetValidOffer_OfferNotFound:
          case SuccessReportTypes.LoyaltyPackageService_GetValidOffer_InvalidOffer:
          case SuccessReportTypes.ReceiptService_createUpdateReceiptWithReceiptItems_MinSpendNotAchieved:
            if (v2Options?.useV2) {
              // Clear the offer the loyaltyV2 way (mirrors cart-calculate's
              // INVALID handling) and surface the popup via the new offer format.
              dispatch(removeLoyaltyOffer());
              dispatch(
                setLoyaltyOfferError({
                  code: res.data.reason,
                  message: res.data?.data?.reason?.message ?? res.data?.message,
                })
              );
            } else {
              dispatch(removeOfferAction());
              dispatch(resetAllOffersArrays());
              offersCache.clear();
              dispatch(setOfferErrorReason(res.data.reason));
            }
            queryParams = [`${URL_PARAMS.OFFER}=true`];
            router.push(
              getPageLink(router, PagesEnum.Cart, { queryParams }).href,
              getPageLink(router, PagesEnum.Cart, { queryParams }).as
            );

            return;

          case SuccessReportTypes.MicrosIntegration_CreatePOSReceipt_OfflinePos:
          case SuccessReportTypes.MicrosIntegration_SendPOSReceipt_Fail_503:
          case SuccessReportTypes.ReceiptController_ConfirmReceipt_PosSystemNotConnected:
            queryParams = [`${URL_PARAMS.ERROR}=true`];
            router.push(
              getPageLink(router, PagesEnum.Cart, { queryParams }).href,
              getPageLink(router, PagesEnum.Cart, { queryParams }).as
            );

            return;
          case SuccessReportTypes.ReceiptController_ConfirmReceipt_CustomerBlocked:
            queryParams = [`${URL_PARAMS.BLOCKED}=true`];
            router.push(
              getPageLink(router, PagesEnum.Cart, { queryParams }).href,
              getPageLink(router, PagesEnum.Cart, { queryParams }).as
            );

            return;

          default:
            alert(
              `${AlertErrorType.Unhandled_ConfirmReceipt_SuccessReportTypes_error}`,
              {
                cause: res.data.reason,
                status: "unhandled_success_report",
              },
              PAGER_DUTY_ERROR_URGENCY.HIGH
            );
            window.location.href = getPageLink(router, PagesEnum.Home, {}).as;

            return;
        }
      }

      offersCache.clear();
      dispatch(resetAllOffersArrays());

      if (res.data.seesionTimeout) {
        dispatch(setPosReceiptPaymentTimeout(res.data.seesionTimeout));
      }

      if (selfService) {
        const payTypes = {
          [PAYMENT_METHOD.CASH]: PayTypeEnum.PAY_IN_STORE,
          [PAYMENT_METHOD.CARD_ON_DELIVERY]: PayTypeEnum.PAY_POS,
          [PAYMENT_METHOD.ONLINE_CARD]: PayTypeEnum.PAY_POS,
          [PAYMENT_METHOD.POS]: PayTypeEnum.PAY_POS,
          [PAYMENT_METHOD.NONE]: PayTypeEnum.PAY_IN_STORE,
        };

        const payType =
          redeemedOffer?.offerType === MAIN_OFFER_TYPE.PERCENTAGE_AMOUNT &&
          "value" in redeemedOffer.details &&
          redeemedOffer.details.value === 100
            ? PayTypeEnum.PAY_SUCCESS
            : payTypes[res.data.paymentMethod as PAYMENT_METHOD];

        if (res.data.timeRemainingToSendAnotherSession) {
          dispatch(
            setTimeRemainingToSendAnotherSession(
              res.data.timeRemainingToSendAnotherSession
            )
          );
          dispatch(setPOSPayType(PayTypeEnum.PAY_PENDING));
        } else {
          dispatch(setPOSPayType(payType));
        }

        router.push(
          getPageLink(router, PagesEnum.PaymentStatus).href,
          getPageLink(router, PagesEnum.PaymentStatus).as
        );

        return;
      }

      dispatch(setSelectedScheduleSlot(undefined));

      if (res.data.paymentMethod === PAYMENT_METHOD.ONLINE_CARD) {
        // Paid with DSQ partner points (prepits/project#4468): core answers
        // with the DSQ webview to open instead of a payment link. The guest
        // goes to the partner-points page, which frames the webview; the
        // token travels in memory only, never in the URL or storage. Nothing
        // is stamped as a pending online payment: there is no link to reopen.
        const opensDsqWebview =
          !res.data.payment_link && Boolean(res.data.dsqpwpWebviewUrl);
        if (loadingStateSolution) {
          dispatch(
            pushToLoadingStack({
              action: LoadingActionEnum.DECREASE,
              functionName: "usePayHelper confirmReceiptRequest",
              date: new Date(),
            })
          );
          dispatch(decreasePendingOperations());
        } else {
          dispatch(setLoading(false));
        }
        if (opensDsqWebview) {
          dispatch(
            startDsqpwpSession({
              webviewUrl: res.data.dsqpwpWebviewUrl,
              accessToken: res.data.dsqpwpAccessToken,
              receiptId: res.data.receiptId,
              receiptUuid: res.data.receiptUuid,
            })
          );
          const partnerPoints = getPageLink(router, PagesEnum.PartnerPoints);
          router.push(partnerPoints.href, partnerPoints.as);

          return;
        }
        // Remember this attempt so retrying the same order reuses the
        // payment session instead of re-confirming a duplicate receipt.
        //
        // A wallet order is stamped too, under a fingerprint no confirm can
        // match: never reopened, but still released on the way back.
        storePendingOnlinePayment({
          fingerprint: usesWallet
            ? walletOrderFingerprint(res.data.receiptId)
            : confirmFingerprint,
          paymentLink: res.data.payment_link,
          receiptId: res.data.receiptId,
          ...(usesWallet ? { usesWallet: true } : {}),
        });
        // The stamp above exists from this line on, but the browser has not gone
        // anywhere yet. Arm the departure watch first so that only a document
        // that really left for the gateway can ever be abandoned on the way back.
        armGatewayDepartureTracking();
        window.location.href = res.data.payment_link;
      } else {
        router.push(
          getPageLink(router, PagesEnum.Pay, { receiptId: res.data.receiptId })
            .href,
          getPageLink(router, PagesEnum.Pay, { receiptId: res.data.receiptId })
            .as
        );
      }
    })
    .catch((e) => {
      if (loadingStateSolution) {
        dispatch(
          pushToLoadingStack({
            action: LoadingActionEnum.DECREASE,
            functionName: "usePayHelper confirmReceiptRequest",
            date: new Date(),
          })
        );
        dispatch(decreasePendingOperations());
      } else {
        dispatch(setLoading(false));
      }
      if (!e.response || !e.response.status) {
        handleGenericError(e, router);
      } else if (e.response.status === 429) {
        // rate Limit
        dispatch(
          setAnimatedGlobalBannerState({
            isOpen: true,
            text: "Please try again in 2 minutes",
            warning: true,
          })
        );
      } else if (e.response.status === 401) {
        return router.push(
          `${
            getPageLink(router, PagesEnum.Login, {
              queryParams: [`${URL_PARAMS.PAY}=true`],
            }).as
          }`
        );
      } else if (
        e.response.status === 453 &&
        e.response.data?.error ===
          LowErrorType.ReceiptController_CreateReceipt_InvalidPhoneNumber
      ) {
        // The branch's POS needs the customer's phone. A guest has none, so
        // the only way through is to sign in with one, as on a 401.
        if (isVisitor()) {
          return router.push(
            `${
              getPageLink(router, PagesEnum.Login, {
                queryParams: [`${URL_PARAMS.PAY}=true`],
              }).as
            }`
          );
        }
        dispatch(
          setAnimatedGlobalBannerState({
            isOpen: true,
            text:
              e.response.data?.userMessage ??
              "Please update your phone number, including the country code, and try again.",
            warning: true,
          })
        );
      } else if (
        e.response.status === 453 &&
        e.response.data?.error ===
          LowErrorType.ReceiptController_CreateReceipt_QuoteDiffersFromCharge
      ) {
        // The total this cart was quoted is not what checkout would charge,
        // and core refused the order rather than charge it. Back to the cart,
        // which re-quotes, so the guest sees the real total before paying.
        dispatch(
          setAnimatedGlobalBannerState({
            isOpen: true,
            text:
              e.response.data?.userMessage ??
              "Your order total has changed. Please review your cart and try again.",
            warning: true,
          })
        );

        return router.push(getPageLink(router, PagesEnum.Cart).href);
      } else if (e.response.status === 452) {
        switch (e.response.data.error) {
          case ErrorType.ItemService_CheckDisabledItemsForSubItems_SomeItemsAreDisabled:
            return router.push(getPageLink(router, PagesEnum.Cart).href);

          case ErrorType.help_getTotalAndDiscountPerPackageOffer_NoDiscountableAmount:
            if (v2Options?.useV2) {
              // loyaltyV2: clear via the new offer classes and reuse the
              // OfferError popup driven by the new offer-error state.
              dispatch(removeLoyaltyOffer());
              dispatch(
                setLoyaltyOfferError({
                  code: e.response.data.error,
                  message: e.response.data?.message,
                })
              );
              const offerQueryParams = [`${URL_PARAMS.OFFER}=true`];
              router.push(
                getPageLink(router, PagesEnum.Cart, {
                  queryParams: offerQueryParams,
                }).href,
                getPageLink(router, PagesEnum.Cart, {
                  queryParams: offerQueryParams,
                }).as
              );

              return;
            }
            const queryParams = [`${URL_PARAMS.EXCLUDED}=true`];
            router.push(
              getPageLink(router, PagesEnum.Cart, { queryParams }).href,
              getPageLink(router, PagesEnum.Cart, { queryParams }).as
            );

            return;

          case ErrorType.ReceiptController_confirmReceipt_handleDeliveryV2_franchiseDeliveryConfigNotFound:
            return router.push(getPageLink(router, redirectPage).href);

          case ErrorType.ScheduleSlotService_GetValidScheduleSlotById_SlotIsUnAvailable:
            dispatch(setSelectedScheduleSlot(undefined));
            router.push(
              getPageLink(router, PagesEnum.Cart).href,
              getPageLink(router, PagesEnum.Cart).as
            );
            dispatch(
              setAnimatedGlobalBannerState({
                isOpen: true,
                text: "The time slot you selected is no longer available",
                warning: true,
              })
            );

            return;

          case ErrorType.ReceiptController_ConfirmReceipt_FoodicsOrderCalculatorFailed:
          case ErrorType.FoodicsService_AddFoodicsOrder_PostFoodicsOrdersFailed:
          case ErrorType.MicrosIntegration_CreatePOSReceipt_Fail:
          case ErrorType.MicrosIntegration_SendPOSReceipt_Fail:
            router.push(
              getPageLink(router, PagesEnum.Cart).href,
              getPageLink(router, PagesEnum.Cart).as
            );
            dispatch(
              setAnimatedGlobalBannerState({
                isOpen: true,
                text: "Unfortunately there is an issue in you order, Please try again",
                warning: true,
              })
            );

            return;
          case ErrorType.ReceiptController_ConfirmReceipt_FranchiseNotLive:
          case ErrorType.FranchiseRepo_getFranchiseFromRestaurantIdAndName_FranchiseOrRestaurantNotFound:
            dispatch(
              setAnimatedGlobalBannerState({
                isOpen: true,
                text: "Please select a valid branch",
                warning: true,
              })
            );

            const pageLink = getPageLink(router, PagesEnum.Home);

            dispatch(setBottomSheetState(true));

            router.push(pageLink.href, pageLink.as);

            break;

          case ErrorType.SubItemRepo_ValidateSubItemAndFranchise_Failure:
            setAnimatedGlobalBannerState({
              isOpen: true,
              text: "Unfortunately there is an issue in you order, Please try again",
              warning: true,
            });

            return router.push(
              getPageLink(router, PagesEnum.Home, { resetQueryParams: true })
                .href,
              getPageLink(router, PagesEnum.Home, { resetQueryParams: true }).as
            );

          default:
            handleGenericError(e, router);
            break;
        }
      } else {
        handleGenericError(e, router);
      }
    });
};

const isNetworkError = (error: unknown): boolean => {
  const axiosError = error as AxiosError | undefined;
  if (!axiosError) {
    return false;
  }

  return (
    !axiosError.response &&
    axiosError.message !== undefined &&
    (axiosError.message.includes("Network Error") ||
      axiosError.code === "ECONNABORTED" ||
      axiosError.code === "ETIMEDOUT" ||
      axiosError.message.includes("timeout") ||
      axiosError.message.includes("connection"))
  );
};

const handleGenericError = (error: any, router: NextRouter) => {
  const networkError = isNetworkError(error);
  const response = error?.response;
  alert(
    `${AlertErrorType.HandleGenericError_Unhandled_ConfirmReceipt_error}`,
    {
      cause:
        response?.data?.reason ??
        response?.data?.message ??
        response?.statusText ??
        error?.message ??
        "Unhandled confirm receipt error",
      status: "unhandled_confirm_receipt_error",
      statusCode: response?.status,
      networkError,
    },
    PAGER_DUTY_ERROR_URGENCY.HIGH,
    networkError
  );
  const queryParams = [`${URL_PARAMS.ERROR}=true`];
  router.push(
    getPageLink(router, PagesEnum.Cart, { queryParams }).href,
    getPageLink(router, PagesEnum.Cart, { queryParams }).as
  );
};
